import { HanamiElement, createShadow, define } from '../core/base';
import { getFocusable } from '../core/focus';
import { applyPosition, autoUpdate, computePosition, hidePopover, showPopover, type Placement } from '../core/position';

const css = `:host{position:relative;display:inline-block}.panel{position:fixed;inset:auto;z-index:var(--hn-z-dropdown);margin:0;padding:0;overflow:visible;border:0;background:transparent;color:inherit}.panel:not([data-open]){display:none}.panel[data-open]{animation:pop-in var(--hn-duration-normal) var(--hn-ease-bloom)}.panel[data-placement^="top"]{transform-origin:bottom center}.panel[data-placement^="bottom"]{transform-origin:top center}@keyframes pop-in{from{opacity:0;transform:translateY(var(--shift,-4px)) scale(0.97)}}.panel[data-placement^="top"]{--shift:4px}@media (prefers-reduced-motion:reduce){.panel[data-open]{animation:none}}`;

const html = /* html */ `
  <slot name="trigger"></slot>
  <div class="panel" part="panel"><slot></slot></div>
`;

const ITEM_SELECTOR = '.hn-menu-item, [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

export interface DropdownSelectDetail {
  value: string;
  item: HTMLElement;
}

/**
 * Menu déroulant (ou popover) positionné automatiquement.
 *
 *   <hn-dropdown>
 *     <button slot="trigger" class="hn-btn">Options</button>
 *     <div class="hn-menu"><button class="hn-menu-item" value="edit">Modifier</button></div>
 *   </hn-dropdown>
 *
 * Attributs : open, placement (défaut bottom-start), distance (px), keep-open, disabled.
 * Événements : hn-select (detail.value, detail.item), hn-open, hn-close.
 */
export class HnDropdown extends HanamiElement {
  static readonly tagName = 'hn-dropdown';
  static get observedAttributes() {
    return ['open', 'placement'];
  }

  private panel!: HTMLElement;
  private stopAutoUpdate?: () => void;
  private typeahead = '';
  private typeaheadTimer?: ReturnType<typeof setTimeout>;

  get open(): boolean {
    return this.getBool('open');
  }
  set open(value: boolean) {
    this.setBool('open', value);
  }

  get placement(): Placement {
    return (this.getAttribute('placement') as Placement) || 'bottom-start';
  }
  set placement(value: Placement) {
    this.setAttribute('placement', value);
  }

  /** Élément qui ouvre le menu (bouton du slot "trigger"). */
  get trigger(): HTMLElement | null {
    const assigned = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="trigger"]')?.assignedElements()[0] as HTMLElement | undefined;
    const el = assigned ?? this.querySelector<HTMLElement>(':scope > [slot="trigger"]');
    if (!el) return null;
    return el.matches('button, a[href], [tabindex]') ? el : (getFocusable(el)[0] ?? el);
  }

  get items(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
      el => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true',
    );
  }

  connectedCallback(): void {
    this.upgradeProperties('open', 'placement');
    if (!this.shadowRoot) {
      const root = createShadow(this, css, html);
      this.panel = root.querySelector('.panel')!;
      if ('popover' in this.panel) this.panel.setAttribute('popover', 'manual');
      root.querySelector('slot[name="trigger"]')!.addEventListener('slotchange', () => this.syncTrigger());
      root.querySelector('slot:not([name])')!.addEventListener('slotchange', () => this.syncMenu());
    }
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.syncTrigger();
    this.syncMenu();
    if (this.open) this.showPanel();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.teardown();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.panel || oldValue === newValue) return;
    if (name === 'open') {
      if (this.open) this.showPanel();
      else this.hidePanel();
    } else if (this.open) {
      this.reposition();
    }
  }

  show(): void {
    if (!this.getBool('disabled')) this.open = true;
  }

  hide(): void {
    this.open = false;
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  private syncTrigger(): void {
    const trigger = this.trigger;
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', String(this.open));
    if (this.querySelector(ITEM_SELECTOR)) trigger.setAttribute('aria-haspopup', 'menu');
  }

  private syncMenu(): void {
    const menu = this.querySelector<HTMLElement>(':scope > :not([slot="trigger"])');
    const items = Array.from(this.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
    if (!menu || !items.length) return;
    if (!menu.hasAttribute('role')) menu.setAttribute('role', 'menu');
    for (const item of items) {
      if (!item.hasAttribute('role')) item.setAttribute('role', 'menuitem');
      item.tabIndex = -1;
    }
  }

  private reposition = (): void => {
    const trigger = this.trigger;
    if (!trigger) return;
    applyPosition(this.panel, computePosition(trigger, this.panel, { placement: this.placement, offset: this.getNumber('distance', 6) }));
  };

  private showPanel(): void {
    showPopover(this.panel);
    this.reposition();
    this.stopAutoUpdate?.();
    const trigger = this.trigger;
    if (trigger) this.stopAutoUpdate = autoUpdate(trigger, this.panel, this.reposition);
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('focusin', this.onOutside, true);
    this.syncTrigger();
    this.emit('hn-open');
  }

  private hidePanel(): void {
    const wasOpen = this.panel.hasAttribute('data-open');
    this.teardown();
    this.syncTrigger();
    if (wasOpen) this.emit('hn-close');
  }

  private teardown(): void {
    if (this.panel) hidePopover(this.panel);
    this.stopAutoUpdate?.();
    this.stopAutoUpdate = undefined;
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('focusin', this.onOutside, true);
    this.items.forEach(item => item.removeAttribute('data-highlighted'));
  }

  private onOutside = (event: Event): void => {
    if (!event.composedPath().includes(this)) this.hide();
  };

  private focusItem(item: HTMLElement | undefined): void {
    if (!item) return;
    this.items.forEach(i => i.removeAttribute('data-highlighted'));
    item.setAttribute('data-highlighted', '');
    item.focus();
  }

  private isFromTrigger(event: Event): boolean {
    const slot = this.shadowRoot?.querySelector('slot[name="trigger"]');
    return !!slot && event.composedPath().includes(slot);
  }

  private onClick = (event: MouseEvent): void => {
    if (this.isFromTrigger(event)) {
      this.toggle();
      return;
    }
    const item = (event.target as Element).closest?.(ITEM_SELECTOR) as HTMLElement | null;
    if (!item || !this.contains(item) || item.hasAttribute('disabled') || item.getAttribute('aria-disabled') === 'true') return;
    if (item.getAttribute('role') === 'menuitemcheckbox') item.setAttribute('aria-checked', String(item.getAttribute('aria-checked') !== 'true'));
    const value = (item as HTMLButtonElement).value || item.dataset.value || item.textContent?.trim() || '';
    this.emit<DropdownSelectDetail>('hn-select', { value, item });
    if (!this.hasAttribute('keep-open') && !item.hasAttribute('data-keep-open')) {
      this.hide();
      this.trigger?.focus();
    }
  };

  private onKeydown = (event: KeyboardEvent): void => {
    const items = this.items;
    if (this.isFromTrigger(event)) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) && items.length) {
        event.preventDefault();
        this.show();
        requestAnimationFrame(() => this.focusItem(event.key === 'ArrowUp' ? items[items.length - 1] : items[0]));
      } else if (event.key === 'Escape' && this.open) {
        event.preventDefault();
        this.hide();
      }
      return;
    }
    if (!this.open) return;
    const index = items.indexOf(event.target as HTMLElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(items[(index + 1) % items.length]);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(items[(index - 1 + items.length) % items.length]);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(items[0]);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(items[items.length - 1]);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.hide();
        this.trigger?.focus();
        break;
      case 'Tab':
        this.hide();
        break;
      default:
        if (event.key.length === 1 && /\S/.test(event.key) && items.length) {
          clearTimeout(this.typeaheadTimer);
          this.typeahead += event.key.toLowerCase();
          this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);
          const match = items.find(i => i.textContent?.trim().toLowerCase().startsWith(this.typeahead));
          if (match) this.focusItem(match);
        }
    }
  };
}

define(HnDropdown.tagName, HnDropdown);
